import {
  t,
  type FriendEntry,
  type FriendRequestEntry,
  RESOURCE_INFO,
  ResourceTier,
  lookupConsumable,
  lookupWeapon,
  lookupShield,
  lookupEngine,
  parseConsumableItemId,
  itemLabel,
} from '@viral-space/shared';
import { attachMobileCloseButton } from './ui-shared';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  bg: 'rgba(0,8,0,0.94)',
  panelBg: 'rgba(0,12,0,0.8)',
  dim: 'rgba(0,255,65,0.3)',
  dimText: 'rgba(0,255,65,0.55)',
  rowHover: 'rgba(0,255,65,0.08)',
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 6px ${COLORS.primary}, 0 0 12px rgba(0,255,65,0.3)`;
const BOX_GLOW = `0 0 8px rgba(0,255,65,0.4), inset 0 0 8px rgba(0,255,65,0.05)`;

type TabId = 'friends' | 'requests';

export interface FriendsCallbacks {
  /** C→S add by callsign (uses the `targetName` lookup path). */
  onAdd: (name: string) => void;
  onAccept: (requestId: string) => void;
  onReject: (requestId: string) => void;
  onRemove: (friendKey: string) => void;
  /**
   * Open a Direct chat with this pilot. Wired in main.ts to the existing
   * Chat instance so we don't need a separate DM thread store here.
   */
  onOpenDM: (friendName: string) => void;
  /**
   * Send a gift (player-to-player trade). The panel collects the
   * recipient + item + quantity then delegates the actual network call
   * to the host so we don't have to plumb the network client into the
   * UI layer.
   */
  onSendGift: (recipientName: string, itemId: string, quantity: number) => void;
}

interface InventoryView {
  items: Array<{ itemId: string; quantity: number }>;
}

/**
 * Friends + requests panel. Opens with a hotkey (default `KeyU` —
 * "users"). Shows two tabs:
 *
 *   1. FRIENDS  — online status, DM, GIFT, REMOVE
 *   2. REQUESTS — incoming (accept/reject) and outgoing (cancel-by-reject)
 *
 * Selecting GIFT pops a small in-panel modal that lets the pilot pick
 * an item out of their cargo + a quantity (with a suggested cost
 * computed from the shared helper). Equipment gifts are clamped to a
 * single unit per server contract.
 */
export class FriendsPanel {
  private container: HTMLElement;
  private root!: HTMLElement;
  private tabBar!: HTMLElement;
  private tabContent!: HTMLElement;
  private addInput!: HTMLInputElement;
  private addButton!: HTMLElement;
  private giftOverlay!: HTMLElement;
  private tabButtons = new Map<TabId, HTMLElement>();
  private activeTab: TabId = 'friends';
  private _visible = false;

  /** Pulled fresh every render so the panel always shows live state. */
  getFriends: (() => FriendEntry[]) | null = null;
  getIncomingRequests: (() => FriendRequestEntry[]) | null = null;
  getOutgoingRequests: (() => FriendRequestEntry[]) | null = null;
  getInventory: (() => InventoryView) | null = null;
  callbacks: FriendsCallbacks | null = null;
  onClose: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.build();
  }

  get visible(): boolean {
    return this._visible;
  }

  toggle(): void {
    this._visible ? this.hide() : this.show();
  }

  show(): void {
    if (this._visible) return;
    this._visible = true;
    this.root.style.display = 'flex';
    this.refresh();
  }

  hide(): void {
    if (!this._visible) return;
    this._visible = false;
    this.root.style.display = 'none';
    this.closeGiftModal();
    this.onClose?.();
  }

  refresh(): void {
    if (!this._visible) return;
    this.renderActiveTab();
  }

  /**
   * Pre-populate the "add friend" input with a callsign — used by the
   * world-target "send friend request" affordance, so the player can
   * confirm/edit before sending.
   */
  prefillAddName(name: string): void {
    this.addInput.value = name;
    this.show();
    this.activeTab = 'friends';
    this.renderActiveTab();
    this.addInput.focus();
  }

  private el(
    tag: string,
    styles: Partial<CSSStyleDeclaration>,
    text?: string,
  ): HTMLElement {
    const e = document.createElement(tag);
    Object.assign(e.style, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build(): void {
    this.root = this.el('div', {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(560px, 92vw)',
      maxHeight: '88vh',
      background: COLORS.bg,
      border: `1px solid ${COLORS.dim}`,
      boxShadow: BOX_GLOW,
      padding: '14px 16px',
      fontFamily: FONT,
      color: COLORS.primary,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      display: 'none',
      flexDirection: 'column',
      gap: '10px',
      zIndex: '60',
      pointerEvents: 'auto',
    });
    this.container.appendChild(this.root);

    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${COLORS.dim}`,
      paddingBottom: '6px',
      flexShrink: '0',
    });
    header.appendChild(
      this.el('div', {
        fontSize: '16px',
        textShadow: GLOW,
        letterSpacing: '3px',
      }, t('friends.title')),
    );
    const closeBtn = this.el('button', {
      fontFamily: FONT,
      fontSize: '12px',
      background: 'transparent',
      border: `1px solid ${COLORS.danger}`,
      color: COLORS.danger,
      padding: '4px 10px',
      cursor: 'pointer',
      letterSpacing: '2px',
    }, 'X');
    closeBtn.addEventListener('click', () => this.hide());
    header.appendChild(closeBtn);
    this.root.appendChild(header);

    const addRow = this.el('div', {
      display: 'flex',
      gap: '6px',
      alignItems: 'center',
    });
    this.addInput = document.createElement('input');
    this.addInput.type = 'text';
    this.addInput.maxLength = 32;
    this.addInput.placeholder = t('friends.addPlaceholder');
    Object.assign(this.addInput.style, {
      flex: '1',
      fontFamily: FONT,
      fontSize: '12px',
      padding: '6px 8px',
      background: 'rgba(0,8,0,0.85)',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.primary,
      outline: 'none',
      letterSpacing: '1px',
      textTransform: 'none',
    });
    this.addInput.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.submitAdd();
    });
    addRow.appendChild(this.addInput);
    this.addButton = this.makeBtn(t('friends.addAction'), () => this.submitAdd(), COLORS.primary);
    addRow.appendChild(this.addButton);
    this.root.appendChild(addRow);

    this.tabBar = this.el('div', {
      display: 'flex',
      gap: '0',
      borderBottom: `1px solid ${COLORS.dim}`,
      flexShrink: '0',
    });
    this.root.appendChild(this.tabBar);

    const tabs: Array<{ id: TabId; key: string }> = [
      { id: 'friends', key: 'friends.tabFriends' },
      { id: 'requests', key: 'friends.tabRequests' },
    ];
    for (const tab of tabs) {
      const btn = this.el('button', {
        fontFamily: FONT,
        fontSize: '11px',
        padding: '6px 14px',
        background: 'transparent',
        border: 'none',
        borderBottom: `1px solid ${COLORS.dim}`,
        color: COLORS.dimText,
        cursor: 'pointer',
        letterSpacing: '2px',
        flex: '1',
      }, t(tab.key));
      btn.addEventListener('click', () => this.switchTab(tab.id));
      this.tabBar.appendChild(btn);
      this.tabButtons.set(tab.id, btn);
    }

    this.tabContent = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      overflowY: 'auto',
      paddingRight: '4px',
      minHeight: '160px',
    });
    this.root.appendChild(this.tabContent);

    this.giftOverlay = this.el('div', {
      position: 'absolute',
      inset: '0',
      background: 'rgba(0,8,0,0.96)',
      border: `1px solid ${COLORS.dim}`,
      padding: '14px 16px',
      display: 'none',
      flexDirection: 'column',
      gap: '10px',
      zIndex: '5',
    });
    this.root.appendChild(this.giftOverlay);

    attachMobileCloseButton(this.root, () => this.hide(), {
      hideOnMobile: [closeBtn],
    });
  }

  private switchTab(id: TabId): void {
    this.activeTab = id;
    this.renderActiveTab();
  }

  private renderActiveTab(): void {
    for (const [id, btn] of this.tabButtons) {
      const active = id === this.activeTab;
      btn.style.color = active ? COLORS.primary : COLORS.dimText;
      btn.style.textShadow = active ? GLOW : 'none';
      btn.style.borderBottomColor = active ? 'transparent' : COLORS.dim;
    }
    this.tabContent.innerHTML = '';
    if (this.activeTab === 'friends') {
      this.renderFriends();
    } else {
      this.renderRequests();
    }
  }

  private submitAdd(): void {
    const name = this.addInput.value.trim();
    if (!name) return;
    this.callbacks?.onAdd(name);
    this.addInput.value = '';
  }

  private renderFriends(): void {
    const friends = (this.getFriends?.() ?? []).slice();
    if (friends.length === 0) {
      this.tabContent.appendChild(
        this.el('div', {
          color: COLORS.dim,
          fontSize: '11px',
          textAlign: 'center',
          padding: '30px 8px',
          textTransform: 'none',
          lineHeight: '1.4',
        }, t('friends.empty')),
      );
      return;
    }
    friends.sort((a, b) => {
      if (a.online !== b.online) return a.online ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    for (const friend of friends) {
      this.tabContent.appendChild(this.buildFriendRow(friend));
    }
  }

  private buildFriendRow(friend: FriendEntry): HTMLElement {
    const row = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 10px',
      border: `1px solid ${COLORS.dim}`,
      background: COLORS.panelBg,
    });
    const dot = this.el('div', {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: friend.online ? COLORS.primary : COLORS.dim,
      boxShadow: friend.online ? `0 0 6px ${COLORS.primary}` : 'none',
      flexShrink: '0',
    });
    row.appendChild(dot);
    const text = this.el('div', {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      minWidth: '0',
    });
    text.appendChild(
      this.el('div', {
        fontSize: '12px',
        textShadow: friend.online ? GLOW : 'none',
        color: friend.online ? COLORS.primary : COLORS.dimText,
        letterSpacing: '1px',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }, friend.name),
    );
    text.appendChild(
      this.el('div', {
        fontSize: '9px',
        color: COLORS.dim,
        textTransform: 'none',
      }, friend.online ? t('friends.statusOnline') : this.formatLastSeen(friend.lastSeen)),
    );
    row.appendChild(text);

    const actions = this.el('div', { display: 'flex', gap: '4px' });
    if (friend.online) {
      actions.appendChild(
        this.makeBtn(t('friends.actionDm'), () => {
          this.callbacks?.onOpenDM(friend.name);
          this.hide();
        }, COLORS.primary, true),
      );
      actions.appendChild(
        this.makeBtn(t('friends.actionGift'), () => this.openGiftModal(friend.name), COLORS.secondary, true),
      );
    }
    actions.appendChild(
      this.makeBtn(t('friends.actionRemove'), () => {
        this.callbacks?.onRemove(friend.key);
      }, COLORS.danger, true),
    );
    row.appendChild(actions);
    return row;
  }

  private formatLastSeen(ts: number): string {
    if (!ts) return t('friends.statusOfflineUnknown');
    const diffMs = Date.now() - ts;
    if (diffMs < 60_000) return t('friends.statusJustNow');
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 60) return t('friends.statusMinutesAgo', { n: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('friends.statusHoursAgo', { n: hours });
    const days = Math.floor(hours / 24);
    return t('friends.statusDaysAgo', { n: days });
  }

  private renderRequests(): void {
    const incoming = this.getIncomingRequests?.() ?? [];
    const outgoing = this.getOutgoingRequests?.() ?? [];
    if (incoming.length === 0 && outgoing.length === 0) {
      this.tabContent.appendChild(
        this.el('div', {
          color: COLORS.dim,
          fontSize: '11px',
          textAlign: 'center',
          padding: '30px 8px',
          textTransform: 'none',
        }, t('friends.requestsEmpty')),
      );
      return;
    }

    if (incoming.length > 0) {
      this.tabContent.appendChild(this.buildSectionLabel(t('friends.incomingHeading')));
      for (const req of incoming) {
        this.tabContent.appendChild(this.buildIncomingRequestRow(req));
      }
    }
    if (outgoing.length > 0) {
      this.tabContent.appendChild(this.buildSectionLabel(t('friends.outgoingHeading')));
      for (const req of outgoing) {
        this.tabContent.appendChild(this.buildOutgoingRequestRow(req));
      }
    }
  }

  private buildSectionLabel(label: string): HTMLElement {
    return this.el('div', {
      fontSize: '10px',
      color: COLORS.secondary,
      letterSpacing: '2px',
      marginTop: '4px',
    }, label);
  }

  private buildIncomingRequestRow(req: FriendRequestEntry): HTMLElement {
    const row = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 10px',
      border: `1px solid ${COLORS.dim}`,
      background: COLORS.panelBg,
    });
    row.appendChild(
      this.el('div', {
        flex: '1',
        fontSize: '12px',
        textShadow: GLOW,
      }, req.fromName),
    );
    row.appendChild(
      this.makeBtn(t('friends.requestAccept'), () => {
        this.callbacks?.onAccept(req.id);
      }, COLORS.primary, true),
    );
    row.appendChild(
      this.makeBtn(t('friends.requestReject'), () => {
        this.callbacks?.onReject(req.id);
      }, COLORS.danger, true),
    );
    return row;
  }

  private buildOutgoingRequestRow(req: FriendRequestEntry): HTMLElement {
    const row = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 10px',
      border: `1px solid ${COLORS.dim}`,
      background: COLORS.panelBg,
    });
    row.appendChild(
      this.el('div', {
        flex: '1',
        fontSize: '12px',
        color: COLORS.dimText,
      }, req.toName),
    );
    row.appendChild(
      this.el('div', {
        fontSize: '10px',
        color: COLORS.dim,
        letterSpacing: '2px',
      }, t('friends.requestPending')),
    );
    row.appendChild(
      this.makeBtn(t('friends.requestCancel'), () => {
        this.callbacks?.onReject(req.id);
      }, COLORS.danger, true),
    );
    return row;
  }

  /* ───── Gift modal (player-to-player trade) ───── */

  private openGiftModal(recipientName: string): void {
    const inv = this.getInventory?.() ?? { items: [] };
    type Entry = { itemId: string; label: string; max: number };
    const entries: Entry[] = [];
    for (const slot of inv.items) {
      if (slot.quantity <= 0) continue;
      entries.push({
        itemId: slot.itemId,
        label: this.labelFor(slot.itemId),
        max: slot.quantity,
      });
    }
    entries.sort((a, b) => a.label.localeCompare(b.label));

    this.giftOverlay.innerHTML = '';
    this.giftOverlay.style.display = 'flex';

    const head = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${COLORS.dim}`,
      paddingBottom: '6px',
    });
    head.appendChild(
      this.el('div', {
        fontSize: '14px',
        textShadow: GLOW,
        letterSpacing: '2px',
      }, t('trade.title', { name: recipientName })),
    );
    const closeBtn = this.makeBtn('X', () => this.closeGiftModal(), COLORS.danger, true);
    head.appendChild(closeBtn);
    this.giftOverlay.appendChild(head);

    if (entries.length === 0) {
      this.giftOverlay.appendChild(
        this.el('div', {
          color: COLORS.dim,
          textAlign: 'center',
          padding: '30px 8px',
          fontSize: '11px',
          textTransform: 'none',
        }, t('trade.emptyInventory')),
      );
      return;
    }

    let selected: Entry | null = entries[0];
    const list = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      maxHeight: '200px',
      overflowY: 'auto',
      paddingRight: '4px',
    });
    const rowEls: HTMLElement[] = [];
    const updateSelection = (entry: Entry, idx: number) => {
      selected = entry;
      qtyInput.max = String(entry.max);
      if (Number(qtyInput.value) > entry.max) qtyInput.value = String(entry.max);
      qtyHint.textContent = t('trade.maxQty', { n: entry.max });
      for (let i = 0; i < rowEls.length; i++) {
        rowEls[i].style.background = i === idx ? COLORS.rowHover : 'transparent';
        rowEls[i].style.color = i === idx ? COLORS.primary : COLORS.dimText;
      }
    };

    const buildRow = (entry: Entry, idx: number): HTMLElement => {
      const row = this.el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 8px',
        cursor: 'pointer',
        fontSize: '11px',
        color: COLORS.dimText,
        border: `1px solid ${COLORS.dim}`,
      });
      row.appendChild(this.el('span', { textTransform: 'none' }, entry.label));
      row.appendChild(this.el('span', { color: COLORS.secondary }, `x${entry.max}`));
      row.addEventListener('click', () => updateSelection(entry, idx));
      return row;
    };

    for (let i = 0; i < entries.length; i++) {
      const row = buildRow(entries[i], i);
      list.appendChild(row);
      rowEls.push(row);
    }
    this.giftOverlay.appendChild(list);

    const qtyRow = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px',
    });
    qtyRow.appendChild(this.el('span', { fontSize: '11px' }, t('trade.qtyLabel')));
    const qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.min = '1';
    qtyInput.value = '1';
    Object.assign(qtyInput.style, {
      width: '80px',
      fontFamily: FONT,
      fontSize: '12px',
      padding: '4px 6px',
      background: 'rgba(0,8,0,0.85)',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.primary,
      outline: 'none',
    });
    qtyInput.addEventListener('keydown', (e) => e.stopPropagation());
    qtyRow.appendChild(qtyInput);
    const qtyHint = this.el('span', {
      fontSize: '10px',
      color: COLORS.dim,
    }, '');
    qtyRow.appendChild(qtyHint);
    this.giftOverlay.appendChild(qtyRow);

    const actions = this.el('div', {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end',
      marginTop: '8px',
    });
    actions.appendChild(
      this.makeBtn(t('trade.cancel'), () => this.closeGiftModal(), COLORS.dim),
    );
    actions.appendChild(
      this.makeBtn(t('trade.send'), () => {
        if (!selected) return;
        const qty = Math.max(1, Math.min(selected.max, Number(qtyInput.value) | 0));
        this.callbacks?.onSendGift(recipientName, selected.itemId, qty);
        this.closeGiftModal();
      }, COLORS.primary),
    );
    this.giftOverlay.appendChild(actions);

    if (entries.length > 0) updateSelection(entries[0], 0);
  }

  private closeGiftModal(): void {
    this.giftOverlay.style.display = 'none';
    this.giftOverlay.innerHTML = '';
  }

  private labelFor(itemId: string): string {
    const resourceMatch = itemId.match(/^resource_(\d+)$/);
    if (resourceMatch) {
      const tier = parseInt(resourceMatch[1], 10) as ResourceTier;
      const info = RESOURCE_INFO[tier];
      if (info) return info.name;
    }
    const consumableId = parseConsumableItemId(itemId);
    if (consumableId) {
      const def = lookupConsumable(consumableId);
      if (def) return itemLabel('consumable', consumableId, def.name);
    }
    const w = lookupWeapon(itemId);
    if (w) return itemLabel('weapon', itemId, w.name);
    const s = lookupShield(itemId);
    if (s) return itemLabel('shield', itemId, s.name);
    const e = lookupEngine(itemId);
    if (e) return itemLabel('engine', itemId, e.name);
    return itemId;
  }

  private makeBtn(
    label: string,
    onClick: () => void,
    color: string,
    compact = false,
  ): HTMLElement {
    const btn = this.el('button', {
      fontFamily: FONT,
      fontSize: compact ? '10px' : '11px',
      padding: compact ? '4px 8px' : '5px 12px',
      background: 'transparent',
      border: `1px solid ${color}`,
      color,
      cursor: 'pointer',
      letterSpacing: '2px',
      transition: 'all 0.15s',
    }, label);
    btn.addEventListener('mouseenter', () => {
      btn.style.background = color;
      btn.style.color = '#000';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'transparent';
      btn.style.color = color;
    });
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }
}
